import { type ReactElement } from 'react'
import { isDefined, isDeleted, type AppId, type AppScriptId, type ScriptData } from '@terros/common'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import { useForm } from '@mantine/form'
import { Stack } from '@mantine/core'
import { useToastError } from '@hooks'
import {
  useConnectApp,
  useConnectScript,
  useConnectVersion,
  useRemoveConnectScript,
  useTranslation,
} from '@front/model'
import { DangerZone, NotReady, Page, TAlert, TAuditInfoSection, TButton, TListItem } from '@components'
import { parseConnectAppId, parseConnectScriptId, buildConnectAppEditorHref } from '../integration/util'
import { ConnectAccessGuard } from '../integration/access'
import { toScriptFormValues, type ScriptFormValues } from './components/scriptForm'
import { ScriptBasicsCard } from './components/ScriptBasicsCard'

function ConnectScriptPage(): ReactElement {
  const { appId, scriptId } = Route.useParams()
  const parsedAppId = parseConnectAppId(appId)
  const parsedScriptId = parseConnectScriptId(scriptId)

  return (
    <ConnectAccessGuard>
      {!parsedScriptId || !parsedAppId ? (
        <NotReady error='Invalid script path' id='connect script' />
      ) : (
        <ScriptDetailsContent scriptId={parsedScriptId} appId={parsedAppId} />
      )}
    </ConnectAccessGuard>
  )
}

function ScriptDetailsContent({ scriptId, appId }: { scriptId: AppScriptId; appId: AppId }): ReactElement {
  const { t } = useTranslation()
  const scriptState = useConnectScript(scriptId)
  const script = scriptState.data
  const effectiveAppId = script?.appId ?? appId
  const versionState = useConnectVersion(effectiveAppId, script?.appVersion, isDefined(script))
  const appState = useConnectApp(effectiveAppId)
  const version = versionState.data?.appVersion

  if (!script) {
    return (
      <NotReady data={script} error={scriptState.error} id={t('connect script')} isLoading={scriptState.isLoading} />
    )
  }

  const canArchive =
    !isDeleted(script) && !isDeleted(version) && !isDeleted(appState.data?.app) && version?.status === 'draft'
  const refetch = (): void => {
    Promise.all([scriptState.refetch(), versionState.refetch(), appState.refetch()])
  }

  return (
    <ScriptDetails
      key={script.scriptId}
      appName={appState.data?.app.name}
      canArchive={canArchive}
      isFetching={scriptState.isFetching || versionState.isFetching || appState.isFetching}
      refetch={refetch}
      script={script}
      showRefreshWarning={Boolean(scriptState.error || appState.error || versionState.error)}
    />
  )
}

type ScriptDetailsProps = {
  appName: string | undefined
  canArchive: boolean
  isFetching: boolean
  refetch: VoidFunction
  script: ScriptData
  showRefreshWarning: boolean
}

function ScriptDetails({
  appName,
  canArchive,
  isFetching,
  refetch,
  script,
  showRefreshWarning,
}: ScriptDetailsProps): ReactElement {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const removeScript = useRemoveConnectScript()
  useToastError(removeScript.error)
  const form = useForm<ScriptFormValues>({ initialValues: toScriptFormValues(script) })

  function handleArchiveScript(): void {
    modals.openConfirmModal({
      centered: true,
      confirmProps: { color: 'red' },
      labels: { cancel: t('Cancel'), confirm: t('Archive script') },
      title: t('Archive {{name}}?', { name: script.name }),
      children: <TAlert>{t('Archived scripts are hidden and can no longer be edited.')}</TAlert>,
      onConfirm: async (): Promise<void> => {
        await removeScript.remove({ scriptId: script.scriptId })
        notifications.show({ color: 'green', message: t('Script archived') })
        navigate({ to: buildConnectAppEditorHref(script.appId, script.appVersion) })
      },
    })
  }

  return (
    <Page>
      <Page.Heading
        back
        isFetching={isFetching}
        refetch={refetch}
        title={t('{{ scriptName }} Script', { scriptName: script.name })}
        breadcrumbs={[
          { href: buildConnectAppEditorHref(script.appId), label: appName ?? t('App') },
          { disabled: true, href: '#', label: script.name },
        ]}
      />
      <Page.Content scrollArea>
        <Stack gap='xl'>
          {showRefreshWarning && (
            <TAlert>
              {t(
                'Refresh failed, some data may be outdated. Please refresh the page to ensure you have the latest data.'
              )}
            </TAlert>
          )}
          <TAlert variant='default'>{t('Scripts are read-only here. Create and upload scripts using the CLI.')}</TAlert>
          <ScriptBasicsCard form={form} readOnly />
          <TAuditInfoSection {...script}>
            <TListItem canCopy detailBold details={script.scriptId} label={t('Script ID')} />
            <TListItem canCopy detailBold details={script.appId} label={t('App ID')} />
          </TAuditInfoSection>
          {canArchive && (
            <DangerZone>
              <TButton loading={removeScript.isRemoving} onClick={handleArchiveScript} variant='destructive'>
                {t('Archive Script')}
              </TButton>
            </DangerZone>
          )}
        </Stack>
      </Page.Content>
    </Page>
  )
}

export const Route = createFileRoute('/integration/developer_/$appId_/$scriptId/')({
  component: ConnectScriptPage,
})
